/**
 * Customer-facing landing page (CDN mode, unauthenticated).
 *
 * Rendered in the language of the domain only (English on discover.indigo.ca,
 * French on lireplus.indigo.ca). The call to action sends the customer to
 * Shopify's default hosted sign-in page; they come back authenticated.
 */

import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageSwitchUrl } from '../../config/siteMode';
import { getLoginUrl } from '../../services/api';
import IndigoLogo from '../IndigoLogo';
import LoadingScreen from '../LoadingScreen';

/**
 * Automated browsers and reduced-motion users get the headline at rest. The
 * animation also runs only once: a headline that keeps rewriting itself never
 * lets the page settle, which stalls browser agents waiting for a stable DOM.
 */
const shouldAnimateHeadline = (): boolean =>
  !globalThis.navigator?.webdriver &&
  !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const TypingText: React.FC<{ text: string }> = ({ text }) => {
  const [animate] = useState(shouldAnimateHeadline);
  const [displayed, setDisplayed] = useState(animate ? '' : text);

  useEffect(() => {
    if (!animate) {
      setDisplayed(text);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let i = 0;
    setDisplayed('');

    const next = () => {
      if (cancelled || i >= text.length) return;
      const jitter = Math.random() * 40 - 20;
      const delay = text[i] === ' ' ? 135 : 55 + jitter;
      timer = setTimeout(() => {
        i++;
        setDisplayed(text.slice(0, i));
        next();
      }, delay);
    };
    timer = setTimeout(next, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, animate]);

  const isTyping = displayed.length < text.length;

  return (
    <>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {displayed}
        {isTyping && <span className="animate-pulse">|</span>}
      </span>
    </>
  );
};

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const authError = new URLSearchParams(globalThis.location.search).get('auth_error');
  const [redirecting, setRedirecting] = useState(false);
  const loginUrl = getLoginUrl(globalThis.location.pathname);

  // A real href keeps the sign-in reachable without running this handler
  // (browser agents, new-tab clicks); a plain click is intercepted only to
  // show the loader first.
  const handleGetStarted = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    setRedirecting(true);
  };

  // Navigate to the Shopify sign-in flow only after the loading screen has
  // painted. Assigning location.href synchronously in the click handler starts
  // the browser navigation before React commits the `redirecting` render, so
  // the loader never shows; a double rAF guarantees a paint frame first. rAF
  // never fires in a background or headless tab, so a timer backs it up.
  useEffect(() => {
    if (!redirecting) return;
    let navigated = false;
    const navigate = () => {
      if (navigated) return;
      navigated = true;
      globalThis.location.href = loginUrl;
    };
    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(navigate);
    });
    const fallback = setTimeout(navigate, 150);
    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      clearTimeout(fallback);
    };
  }, [redirecting, loginUrl]);

  if (redirecting) {
    const loadingMessages = t('onboarding.loading', { returnObjects: true }) as string[];
    return <LoadingScreen messages={loadingMessages} />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between">
        <IndigoLogo />
        <a
          href={getLanguageSwitchUrl()}
          lang={t('header.languageSwitchLang')}
          className="text-sm font-medium text-indigo-brand-text hover:text-indigo-brand-black underline underline-offset-4"
        >
          {t('header.languageSwitch')}
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-5xl text-center">
          <h1 className="text-3xl md:text-5xl font-normal text-indigo-brand-text mb-6 leading-tight md:whitespace-nowrap">
            <TypingText text={t('landing.title')} />
          </h1>
          <p className="text-base md:text-lg text-indigo-brand-text/70 mb-10 max-w-xl mx-auto">
            {t('landing.body')}
          </p>

          {authError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3 text-left">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{t('landing.authError')}</p>
            </div>
          )}

          <a
            href={loginUrl}
            onClick={handleGetStarted}
            className="inline-block px-12 py-4 bg-indigo-brand-text text-white text-base font-semibold tracking-[-0.02em] rounded-lg hover:bg-indigo-brand-black transition-colors"
          >
            {t('landing.cta')}
          </a>
          <p className="mt-6 text-sm text-indigo-brand-text/40 max-w-sm mx-auto">
            {t('landing.footnote')}
          </p>
        </div>
      </main>

      <footer className="bg-indigo-brand-off-white border-t border-indigo-brand-border">
        <div className="container mx-auto px-4 py-6 text-center text-indigo-brand-text/60 text-sm">
          {t('footer.cdn')}
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
